"use client";

import Image from "next/image";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";
import type { ReactNode, Ref } from "react";
import { useCallback, useMemo } from "react";

import { Pagination } from "@/components/our-customers/pagination";
import { GridCross } from "@/components/ui/grid-connector";
import { Link } from "@/components/ui/link";
import { useHorizontalDragScroll } from "@/hooks/use-horizontal-drag-scroll";
import { useHorizontalScrollEdges } from "@/hooks/use-horizontal-scroll-edges";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 6;

const industryParser = parseAsString.withOptions({ history: "replace" });
const pageParser = parseAsInteger.withDefault(1).withOptions({
  history: "replace",
});

export interface CustomerStoryCard {
  slug: string;
  customer: string;
  headline: string;
  preview: string;
  thumbnailSrc: string | null;
  thumbnailAlt: string;
  /** Eligibility-category slug, or null when the industry has no equivalent. */
  industrySlug: string | null;
  industryName: string;
}

interface IndustryOption {
  slug: string;
  name: string;
}

interface CustomerStoriesGridProps {
  title: string;
  allLabel: string;
  clearAllLabel?: string;
  emptyStateLabel?: string;
  customerLabel?: string;
  paginationLabel: string;
  previousPageLabel: string;
  nextPageLabel: string;
  pageLabel: string;
  id?: string;
  cards: CustomerStoryCard[];
  industries: IndustryOption[];
  className?: string;
}

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>) {
  return (node: T | null) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === "function") {
        ref(node);
      } else {
        ref.current = node;
      }
    }
  };
}

function FilterChipScroller({ children }: { children: ReactNode }) {
  const drag = useHorizontalDragScroll<HTMLDivElement>();
  const edges = useHorizontalScrollEdges<HTMLDivElement>();

  const setRef = mergeRefs(drag.ref, edges.ref);

  return (
    <div
      className="relative min-w-0 flex-1"
      data-testid="our-customers-stories-filter-scroller"
    >
      <div
        ref={setRef}
        className={cn(
          "flex gap-2 overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          "touch-pan-x",
          edges.canScrollRight || edges.canScrollLeft
            ? "cursor-grab active:cursor-grabbing"
            : "cursor-default",
          drag.isDragging && "cursor-grabbing select-none"
        )}
        onPointerDown={drag.onPointerDown}
        onPointerMove={drag.onPointerMove}
        onPointerUp={drag.onPointerUp}
        onPointerCancel={drag.onPointerCancel}
        onClickCapture={drag.onClickCapture}
        onScroll={edges.updateEdges}
      >
        {children}
      </div>

      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-white/0 transition-opacity duration-200 motion-reduce:transition-none",
          edges.canScrollLeft ? "opacity-100" : "opacity-0"
        )}
      />
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-white/0 transition-opacity duration-200 motion-reduce:transition-none",
          edges.canScrollRight ? "opacity-100" : "opacity-0"
        )}
      />
    </div>
  );
}

export function CustomerStoriesGrid({
  title,
  allLabel,
  clearAllLabel = "Clear all",
  emptyStateLabel = "No customer stories match this filter yet.",
  paginationLabel,
  previousPageLabel,
  nextPageLabel,
  pageLabel,
  id,
  cards,
  industries,
  className,
}: CustomerStoriesGridProps) {
  const [selectedIndustry, setSelectedIndustry] = useQueryState(
    "industry",
    industryParser
  );
  const [currentPage, setCurrentPage] = useQueryState("page", pageParser);

  const setIndustry = useCallback(
    async (slug: string | null) => {
      await setSelectedIndustry(slug);
      await setCurrentPage(null);
    },
    [setCurrentPage, setSelectedIndustry]
  );

  const filtered = useMemo(
    () =>
      selectedIndustry
        ? cards.filter((c) => c.industrySlug === selectedIndustry)
        : cards,
    [cards, selectedIndustry]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.max(1, Math.min(currentPage, totalPages));
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const visible = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const filterChips = useMemo(
    () => [{ slug: null as string | null, name: allLabel }, ...industries],
    [allLabel, industries]
  );

  const hasActiveFilter = selectedIndustry !== null;

  return (
    <section
      id={id}
      data-testid="our-customers-stories-section"
      className={cn("bg-[#f9f9f8] md:px-12 lg:px-20", className)}
    >
      <div className="relative mx-auto max-w-[1280px] bg-white md:border md:border-[#e5e6e6]">
        <GridCross
          corner="top-left"
          className="absolute -top-px -left-px hidden md:block"
        />
        <GridCross
          corner="top-right"
          className="absolute -top-px -right-px hidden md:block"
        />
        <GridCross
          corner="bottom-left"
          className="absolute -bottom-px -left-px hidden md:block"
        />
        <GridCross
          corner="bottom-right"
          className="absolute -right-px -bottom-px hidden md:block"
        />

        <div className="relative flex items-center justify-between gap-6 border-y border-[#e5e6e6] p-6 md:gap-8 md:border-t-0 md:px-8 md:py-6">
          <FilterChipScroller>
            {filterChips.map((filter) => {
              const isActive =
                filter.slug === null
                  ? !selectedIndustry
                  : selectedIndustry === filter.slug;
              return (
                <button
                  key={filter.slug ?? "all"}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setIndustry(filter.slug)}
                  className={cn(
                    "text-body-xs flex h-8 shrink-0 items-center rounded-lg border px-2.5 whitespace-nowrap text-[#0c0d0d]",
                    isActive
                      ? "border-[#caccce] bg-[#f9f9f8]"
                      : "border-[#e5e6e6] bg-white"
                  )}
                >
                  {filter.name}
                </button>
              );
            })}
          </FilterChipScroller>
          <button
            type="button"
            onClick={() => setIndustry(null)}
            disabled={!hasActiveFilter}
            className={cn(
              "text-body-xs flex h-8 shrink-0 items-center rounded-lg border px-2.5 whitespace-nowrap transition-colors",
              hasActiveFilter
                ? "cursor-pointer border-[#e5e6e6] bg-white text-[#0c0d0d] hover:border-[#caccce]"
                : "cursor-not-allowed border-[#e5e6e6] bg-[#f9f9f8] text-[#62666a]"
            )}
          >
            {clearAllLabel}
          </button>
        </div>

        <div className="flex flex-col gap-6 px-6 py-8 md:gap-8 md:p-10 lg:gap-8 lg:p-16">
          <h2
            className="text-[32px] leading-[1.2] tracking-[-0.04em]"
            style={{ color: "#0c0d0d" }}
          >
            {title}
          </h2>

          {visible.length === 0 ? (
            <p className="text-body-sm text-[#62666a]">{emptyStateLabel}</p>
          ) : (
            <div className="grid grid-cols-1 gap-x-5 gap-y-6 md:grid-cols-2">
              {visible.map((post) => (
                <article key={post.slug} className="group flex flex-col gap-6">
                  <Link
                    href={`/our-customers/${post.slug}`}
                    className="relative block aspect-[285/160] overflow-hidden rounded-2xl border border-[#e5e6e6]"
                  >
                    {post.thumbnailSrc ? (
                      <Image
                        src={post.thumbnailSrc}
                        alt={post.thumbnailAlt}
                        fill
                        sizes="(min-width: 768px) 50vw, 100vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="bg-grey-100 size-full" aria-hidden />
                    )}
                  </Link>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3">
                      <span className="text-body-xs inline-flex items-center rounded-[4px] bg-[#e5e6e6] p-1 leading-none font-medium text-[#0c0d0d]">
                        {post.industryName}
                      </span>
                    </div>
                    <Link
                      href={`/our-customers/${post.slug}`}
                      className="leading-[1.3] group-hover:underline"
                      style={{
                        color: "#0c0d0d",
                        fontSize: "20px",
                        letterSpacing: "-0.8px",
                      }}
                    >
                      {post.customer}
                    </Link>
                    <p className="text-body-sm line-clamp-3 leading-[1.5] tracking-[-0.28px] text-[#62666a]">
                      {post.preview}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          )}

          {totalPages > 1 ? (
            <Pagination
              currentPage={safePage}
              totalPages={totalPages}
              ariaLabel={paginationLabel}
              previousLabel={previousPageLabel}
              nextLabel={nextPageLabel}
              getPageLabel={(page) => `${pageLabel} ${page}`}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}
